import {
  type ComponentType,
  createContext,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
  type DOMAttributes,
  type MutableRefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";

import {
  Box,
  IconButton,
  type IconButtonProps,
  MotionBox,
  motionDurations,
  Row,
  type RowProps,
  SearchIcon,
  Tooltip,
  type TooltipProps,
} from "@hightouch/htui";
import { KBarContext, VisualState, useKBar } from "kbar";
import { AnimatePresence } from "motion/react";

import {
  focusQuietly,
  isFocusLost,
  useIsQuietlyFocused,
} from "src/components/layout/nav/nav-focus";
import { isPopupLayerOpen } from "src/components/layout/popup-layers";
import { usePaletteTriggerTooltip } from "src/components/commandbar/use-palette-trigger-tooltip";
import type { Crumb } from "src/components/layout/types";
import { isTypingTarget } from "src/components/preview-canvas/is-typing-target";
import { NavWidthContext } from "src/contexts/nav-width-context";
import { SidebarClosedIcon } from "src/ui/icons";

/** The sidebar's own shortcut, as Linear's: a plain `[`, for its toggles' tooltips; the mini-nav's provider handles the key. */
export const SIDEBAR_SHORTCUT = "[";

/** The key code a browser gives every key press that an IME is composing, in the browsers that leave `isComposing` unset. */
const IME_KEY_CODE = 229;

/** Marks the sidebar's own sheet or the canvas's nav popover, a layer that the shortcut closes rather than yields to. */
export const MINI_NAV_SHEET_ATTRIBUTE = "data-nav-sheet";

/**
 * A modal, an open popover, a menu, or a list of options is up, so the keyboard is its.
 * Full-surface views that are non-modal dialogs, like the canvas's open file, still take the shortcut.
 * A closed layer can stay mounted, hidden, and a list drawn in the page, not popped up, leaves the shortcut alone.
 */
const isDialogOpen = () =>
  Array.from(document.querySelectorAll('[aria-modal="true"]')).some(
    (modal) => !modal.querySelector(`[${MINI_NAV_SHEET_ATTRIBUTE}]`),
  ) || isPopupLayerOpen(`[${MINI_NAV_SHEET_ATTRIBUTE}]`);

/** Whether a key press is the sidebar's shortcut: a plain `[`, never one typed into a field, composed by an IME or meant for a dialog. */
export const isSidebarShortcut = (event: KeyboardEvent) =>
  event.key === "[" &&
  !event.repeat &&
  !event.defaultPrevented &&
  !event.metaKey &&
  // Layouts that type `[` with AltGr report it as Ctrl+Alt.
  (!event.ctrlKey || event.getModifierState("AltGraph")) &&
  !event.isComposing &&
  event.keyCode !== IME_KEY_CODE &&
  !isTypingTarget(document.activeElement) &&
  !isDialogOpen();

/** A toggle's hit area beyond its 24px box: none, and 44px on a phone, where a finger presses it. */
const TOGGLE_HIT_INSET = { base: -2.5, md: 0 };

/** The nav's small controls, the sidebar and agent panel toggles and the inbox: a 24px box with a 16px glyph, a hover fill, a ring on keyboard focus only. */
export const SIDEBAR_TOGGLE_PROPS = {
  size: "sm",
  variant: "tertiary",
  position: "relative",
  _before: { content: '""', position: "absolute", inset: TOGGLE_HIT_INSET },
  // A pointer's press, or focus a closing panel hands back after one, draws no ring.
  _focus: { boxShadow: "none" },
  _focusVisible: { boxShadow: "outline" },
} as const;

/** Seats the collapsed sidebar's toggle in the card's corner, 8px in from its top and left; the bar keeps its 16px row. */
const TOGGLE_SEAT = { ml: -4, my: -1 } as const;

const LEAD_SHOWN = { opacity: 1, scale: 1, filter: "blur(0px)" };
const LEAD_HIDDEN = { opacity: 0, scale: 0.25, filter: "blur(4px)" };
const LEAD_SWAP = {
  type: "spring",
  duration: motionDurations.slow,
  bounce: 0,
} as const;

type NavSearchButtonProps = Pick<TooltipProps, "placement"> &
  Pick<IconButtonProps, "my">;

/** Opens the command bar, whose own `mod+k` binding opens it too; the bar hands focus back here when it closes. */
export const NavSearchButton = (props: NavSearchButtonProps) =>
  // Outside the command bar's provider there is nothing to open, and kbar's store would throw.
  useContext(KBarContext).query ? <CommandBarSearchButton {...props} /> : null;

const CommandBarSearchButton = ({
  placement,
  ...props
}: NavSearchButtonProps) => {
  const { query, isOpen } = useKBar((state) => ({
    isOpen: state.visualState !== VisualState.hidden,
  }));
  const { isTooltipDisabled, holdTooltip, triggerProps } =
    usePaletteTriggerTooltip();
  return (
    <Tooltip
      message="Open search"
      keyboardShortcut="mod+k"
      placement={placement}
      isDisabled={isTooltipDisabled}
      keepMountedWhileDisabled
    >
      <IconButton
        aria-label="Open search"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-keyshortcuts="Meta+K Control+K"
        icon={SearchIcon}
        {...SIDEBAR_TOGGLE_PROPS}
        {...props}
        {...triggerProps}
        onClick={() => {
          holdTooltip();
          query.toggle();
        }}
      />
    </Tooltip>
  );
};

/** Marks the collapsed sidebar's toggle, so focus can land on it after the sidebar collapses. */
export const MINI_NAV_TRIGGER_SELECTOR = "[data-nav-expand]";

/** Marks the row the collapsed sidebar's toggle sits in, which the peeked sidebar opens below. */
export const MINI_NAV_TOGGLE_ROW_SELECTOR = "[data-nav-toggle-row]";

/** Long enough for a lazy page to load after a link is followed, short enough that a later mount never steals focus. */
const CRUMB_FOCUS_WINDOW_MS = 5000;

/** What the product nav's crumb bars need; it changes only as bars mount, so a peek or a fold never re-renders the pages. */
export type MiniNavContextValue = {
  /** The product nav is beside this page, so its card opens with a crumb bar. */
  hasCrumbBars: boolean;
  /** Counts the crumb bars mounted on this page; with none, the toggle floats over the page. */
  registerCrumbBar: () => () => void;
  /** Counts the loading states on show, over which the toggle never floats: a page with no card has to mount first. */
  registerLoadingState?: () => () => void;
  /** Docks the sidebar again, or opens it as a sheet where it can't dock; a keyboard's sheet lands on the current row. */
  expand: (isKeyboard: boolean) => void;
  /** When the viewer last followed a crumb or sidebar link from the keyboard, so the next page's trigger takes its focus. */
  linkFollowedAt: MutableRefObject<number>;
  /** The trail comes from the provider, so a page with a crumb bar doesn't load the whole nav. */
  Trail: ComponentType<CrumbTrailProps>;
};

export type CrumbTrailProps = {
  crumbs: Crumb[];
  /** Draws the bar's lead, given the page's icon, ahead of the trail. */
  renderLead: (icon: Crumb["icon"]) => ReactNode;
};

export const MiniNavContext = createContext<MiniNavContextValue | null>(null);

/** What changes as the sidebar folds, apart from `MiniNavContext`, so a fold re-renders the crumb bars and not the pages around them. */
export type MiniNavTriggerValue = {
  /** The sidebar is collapsed on this page, so the crumb bar leads with its toggle. */
  hasTrigger: boolean;
  /** The sidebar is sliding in to dock, so the toggle fades out with the slide, ahead of leaving as the dock commits. */
  isDocking?: boolean;
  /** Peeks the sidebar while the pointer rests on its toggle, where the sidebar can dock. */
  peekTrigger?: Pick<
    DOMAttributes<HTMLElement>,
    "onPointerEnter" | "onPointerMove" | "onPointerLeave" | "onPointerDown"
  >;
};

export const MiniNavTriggerContext = createContext<MiniNavTriggerValue>({
  hasTrigger: false,
});

const useMiniNavContext = () => {
  const context = useContext(MiniNavContext);
  if (!context) throw new Error("MiniNavTrigger must be used within Layout");
  return context;
};

/** The sidebar the mini-nav shows, and how: docked beside the page, peeked over it, or as a phone's sheet. */
type MiniNavSidebarValue = {
  overlay: "peek" | "sheet" | null;
  /** Docks a peeked or collapsed sidebar, or collapses a docked one, folding the page along with it. */
  toggleDocked: (source: "toggle" | "shortcut", isKeyboard: boolean) => void;
};

/**
 * Whether a trail the page didn't pass shows: only while the sidebar is collapsed, since the docked sidebar already
 * says where the page is. It flips as a fold starts, either way, so the bar glides with the fold.
 */
export const GeneratedCrumbBarContext = createContext(false);

/** Whether the collapsed sidebar is peeked, apart from the crumb bars' context, so a peek re-renders only the toggle. */
export const MiniNavPeekContext = createContext(false);

/** Set inside the mini-nav's sidebar, so its own toggle folds the page and reads as the overlay it is. */
export const MiniNavSidebarContext = createContext<MiniNavSidebarValue | null>(
  null,
);

/**
 * Registers a crumb bar for the product nav and returns whether this is one: the first row of the page card, holding the toggle while the sidebar is collapsed.
 * Registered before paint, so the floating toggle never draws a frame over a page whose bar holds it.
 */
export const useCrumbBar = (isCandidate = true) => {
  const context = useContext(MiniNavContext);
  const isCrumbBar = isCandidate && context?.hasCrumbBars === true;
  const registerCrumbBar = context?.registerCrumbBar;
  useLayoutEffect(() => {
    if (!isCrumbBar || !registerCrumbBar) return;
    return registerCrumbBar();
  }, [isCrumbBar, registerCrumbBar]);
  return isCrumbBar;
};

/** Marks a loading state on show, so the collapsed sidebar's toggle never floats over it. */
export const useHoldsFloatingToggle = () => {
  const register = useContext(MiniNavContext)?.registerLoadingState;
  // Before paint, so the toggle never draws a frame over the loading state.
  useLayoutEffect(() => register?.(), [register]);
};

/** True beside the product nav, where every page card opens with a crumb bar. */
export const useHasCrumbBars = () =>
  useContext(MiniNavContext)?.hasCrumbBars === true;

/** True while the product nav's sidebar is collapsed on this page, so its crumb bar leads with the toggle. */
export const useIsMiniNavCollapsed = () =>
  useContext(MiniNavTriggerContext).hasTrigger;

/** The collapsed sidebar's toggle, first in the page's crumb bar: it docks the sidebar again. */
export const MiniNavTrigger = ({
  isSeated = true,
}: {
  /** Seats itself in the card's corner; a parent that seats it, as the crumb bar's lead does, passes false. */
  isSeated?: boolean;
}) => {
  const { expand, linkFollowedAt } = useMiniNavContext();
  const { peekTrigger } = useContext(MiniNavTriggerContext);
  // Read without the provider's guard: a page drawn outside the app shell, as in a test, can always dock.
  const { canNavOpen } = useContext(NavWidthContext);
  const quietFocus = useIsQuietlyFocused();
  const triggerRef = useRef<HTMLButtonElement>(null);

  // A followed crumb or sidebar link leaves with the page, so focus falls to the body; the next page's trigger picks it up.
  useEffect(() => {
    if (
      Date.now() - linkFollowedAt.current < CRUMB_FOCUS_WINDOW_MS &&
      isFocusLost()
    ) {
      if (triggerRef.current) {
        focusQuietly(triggerRef.current, { preventScroll: true });
      }
    }
  }, [linkFollowedAt]);

  return (
    <Tooltip
      message="Expand sidebar"
      keyboardShortcut={SIDEBAR_SHORTCUT}
      // Beside the toggle, clear of the peek a rest on it opens below its row, so the two show together.
      placement="right"
      // Where the sidebar can't dock, as on a phone, the toggle opens a sheet, and the sheet hands focus back to it on close.
      isDisabled={!canNavOpen || quietFocus.isQuiet}
      keepMountedWhileDisabled
    >
      <IconButton
        ref={triggerRef}
        data-nav-expand
        aria-label="Expand sidebar"
        aria-keyshortcuts="["
        // Its icon's rail slides as the pointer rests on it.
        data-group
        icon={SidebarClosedIcon}
        {...SIDEBAR_TOGGLE_PROPS}
        {...(isSeated && TOGGLE_SEAT)}
        // As round as the nav's rows, so it reads as one of them.
        borderRadius="lg"
        onFocus={quietFocus.onFocus}
        onBlur={quietFocus.onBlur}
        {...peekTrigger}
        // A press with no pointer position is a keyboard's.
        onClick={(event) => expand(event.detail === 0)}
      />
    </Tooltip>
  );
};

/** A product crumb bar's lead and trail: the toggle while the sidebar is collapsed, or else the page icon, then the trail under its nav place. */
export const MiniNavCrumbTrail = ({
  crumbs,
}: Omit<CrumbTrailProps, "renderLead">) => {
  const { Trail } = useMiniNavContext();
  return (
    // On a phone, the trail starts past the toggle's hit area.
    <Row
      align="center"
      gap={{ base: 2.5, md: 1 }}
      // The trail fills the bar, so Breadcrumbs measures the room it has, not the width it last drew.
      flex={1}
      minWidth={0}
    >
      <Trail crumbs={crumbs} renderLead={renderCrumbBarLead} />
    </Row>
  );
};

const renderCrumbBarLead = (icon: Crumb["icon"]) => (
  <CrumbBarLead icon={icon} />
);

/**
 * The page's icon, or the toggle while the sidebar is collapsed, cross-faded in one slot as the fold starts, so the
 * trail beside them never moves. Both draw at the nav's 16px; each grows in from a quarter size as it unblurs and fades in.
 */
const CrumbBarLead = ({ icon }: { icon: Crumb["icon"] }) => {
  const { hasTrigger, isDocking } = useContext(MiniNavTriggerContext);
  const isToggleShown = hasTrigger && !isDocking;
  return (
    <Box display="grid" flexShrink={0}>
      {icon && (
        <MotionBox
          aria-hidden
          gridArea="1 / 1"
          display="grid"
          placeItems="center"
          boxSize={6}
          {...TOGGLE_SEAT}
          initial={false}
          animate={isToggleShown ? LEAD_HIDDEN : LEAD_SHOWN}
          transition={LEAD_SWAP}
        >
          <Box as={icon} boxSize={4} color="text.tertiary" />
        </MotionBox>
      )}
      {/* Mounted only while the sidebar is collapsed, so focus left on it falls to the body as it docks, as the fold expects. */}
      <AnimatePresence initial={false}>
        {hasTrigger && (
          <MotionBox
            key="toggle"
            gridArea="1 / 1"
            display="flex"
            {...TOGGLE_SEAT}
            pointerEvents={isToggleShown ? undefined : "none"}
            initial={LEAD_HIDDEN}
            animate={isToggleShown ? LEAD_SHOWN : LEAD_HIDDEN}
            transition={LEAD_SWAP}
          >
            <MiniNavTrigger isSeated={false} />
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

/** Where a slot sits: in a crumb bar, whose controls take the sidebar toggle's small size while that toggle leads it, at the end of a title row, or at the end of a floating pill, as the canvas's, where the toggle draws round and stays while its panel is open. */
export type CrumbBarEndSlotPlacement = "crumbBar" | "titleRow" | "pill";

type RegisteredSlot = {
  element: HTMLElement;
  placement: CrumbBarEndSlotPlacement;
};

/** The right end of the page's crumb bar, where the page's agent panel puts its toggle while the panel is closed. */
export const CrumbBarEndSlotContext = createContext<{
  slot: HTMLElement | null;
  /** Where the slot on show sits, so the controls put in it size to their row; null with no slot. */
  placement: CrumbBarEndSlotPlacement | null;
  /** Adds a bar's slot, the latest on top and a floating pill's over any bar's it covers, and returns its removal, so a bar leaving never takes another's slot with it. */
  registerSlot: (
    slot: HTMLElement,
    placement: CrumbBarEndSlotPlacement,
  ) => () => void;
}>({ slot: null, placement: null, registerSlot: () => () => {} });

/** The slots on show, the latest mounted last, for `CrumbBarEndSlotContext`. */
export const useCrumbBarEndSlots = () => {
  const [slots, setSlots] = useState<RegisteredSlot[]>([]);
  const registerSlot = useCallback(
    (element: HTMLElement, placement: CrumbBarEndSlotPlacement) => {
      setSlots((previous) => [...previous, { element, placement }]);
      return () =>
        setSlots((previous) => previous.filter((it) => it.element !== element));
    },
    [],
  );
  const latest =
    slots.findLast((it) => it.placement === "pill") ?? slots.at(-1);
  return useMemo(
    () => ({
      slot: latest?.element ?? null,
      placement: latest?.placement ?? null,
      registerSlot,
    }),
    [latest, registerSlot],
  );
};

/** The crumb bar's right end: the page's own controls, then the agent panel's toggle, which mirrors the sidebar's at the left end. */
export const CrumbBarEnd = ({
  children,
  hasSlot = true,
}: {
  children?: ReactNode;
  /** Off while the bar is folded away, so the agent panel's toggle goes to the title row's end instead. */
  hasSlot?: boolean;
}) => (
  // Controls up to 32px tall, which the bar holds at the height its 16px trail gives it.
  <Row align="center" gap={2} ml="auto" pl={4} my={-2} flexShrink={0}>
    {children}
    {hasSlot && <CrumbBarEndSlot />}
  </Row>
);

/** Where the agent panel's toggle goes, last in a crumb bar's row of controls, or at the end of a title row. */
export const CrumbBarEndSlot = ({
  placement = "crumbBar",
  ...props
}: RowProps & { placement?: CrumbBarEndSlotPlacement }) => {
  const { registerSlot } = useContext(CrumbBarEndSlotContext);
  const [element, setElement] = useState<HTMLElement | null>(null);
  // Before paint, so the agent panel's toggle never draws a frame outside the bar.
  useLayoutEffect(() => {
    if (!element) return;
    return registerSlot(element, placement);
  }, [element, placement, registerSlot]);
  return (
    <Row
      ref={setElement}
      align="center"
      // On a phone, the toggles' 44px hit areas meet without overlapping.
      gap={{ base: 5, md: 2 }}
      // The last glyph, not its hover box, lines up with the card's inset, as the sidebar's toggle's does at the left end.
      mr={-1}
      _empty={{ display: "none" }}
      {...props}
    />
  );
};
